<template>
  <div>
    <div class="mb-4 d-flex align-center">
      <h2>{{ $t('features.nursery-web-account-management-children') }}</h2>
      <v-spacer></v-spacer>
      <v-menu offset-y>
        <template v-slot:activator="{ props }">
          <v-btn
            class="mr-2"
            color="primary"
            variant="outlined"
            :disabled="isFetching"
            v-bind="props"
          >
            <v-icon start>mdi-tray-arrow-down</v-icon>
            {{ $t('common.download') }}
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="pdfDialog = true">
            <v-list-item-title>{{ $t('children.invitationPdf') }}</v-list-item-title>
          </v-list-item>
          <!--MEMORUの案内状が不要になったためコメントアウト-->
          <!--<v-list-item @click="memoruPdfDialog = true">
            <v-list-item-title>MEMORU案内状PDF</v-list-item-title>
          </v-list-item>-->     
          <v-list-item @click="exportChildrenDialog = true">
            <v-list-item-title>{{ $t('children.childCsv') }}</v-list-item-title>
          </v-list-item>
          <v-list-item :href="importChildSampleCsvUrl">
            <v-list-item-title>{{ $t('common.sampleCsv') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn
        @click="importDialog = true"
        :disabled="isFetching"
        color="primary"
        class="mr-2"
        variant="outlined"
      >
        <v-icon start>mdi-plus</v-icon>
        {{ $t('children.batchImport') }}
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        to="/children/account-create"
        :disabled="isFetching"
      >
        <v-icon start>mdi-plus</v-icon>
        {{ $t('common.create') }}
      </v-btn>
    </div>

    <!-- 検索フォーム -->
    <v-card variant="flat" class="mt-4 pa-2" color="grey-lighten-5">
      <v-container>
        <v-row>
          <v-col cols="4">
            <h5>{{ $t('common.class') }}</h5>
            <class-picker :classes="classes" v-model="classId" color="primary"></class-picker>
          </v-col>
          <v-col cols="4">
            <h5>{{ $t('common.lastName') }}</h5>
            <v-text-field
              variant="outlined"
              density="compact"
              hide-details
              v-model="lastName"
              color="primary"
            ></v-text-field>
          </v-col>
          <v-col cols="4">
            <h5>{{ $t('common.firstName') }}</h5>
            <v-text-field
              variant="outlined"
              density="compact"
              hide-details
              v-model="firstName"
              color="primary"
            ></v-text-field>
          </v-col>
          <v-col cols="4">
            <h5>{{ $t('common.gender') }}</h5>
            <gender-picker v-model="gender" color="primary"></gender-picker>
          </v-col>
          <v-col cols="4">
            <h5>{{ $t('common.age') }}</h5>
            <age-picker v-model="age"></age-picker>
          </v-col>
          <v-col cols="4" align-self="center">
            <v-checkbox
              v-model="searchFilter.isQuit"
              color="primary"
              :label="$t('children.quitSchool')"
            ></v-checkbox>
          </v-col>
        </v-row>
        <v-row>
          <v-spacer></v-spacer>
          <v-btn
            @click="search"
            large
            color="primary"
            :disabled="isFetching"
            class="my-2 search-button"
            variant="flat"
            :loading="isFetching"
            >{{ $t('common.search') }}</v-btn
          >
          <v-spacer></v-spacer>
        </v-row>
      </v-container>
    </v-card>

    <!-- 園児一覧 -->
    <v-data-table
      class="pt-4"
      :items="children"
      v-model="selectedChildren"
      :headers="childrenTableHeaders"
      item-value="childId"
      return-object
      :items-per-page="-1"
      show-select
      :loading="isFetching"
      color="primary"
    >
      <template v-slot:loading>
        <v-skeleton-loader type="table-tbody"></v-skeleton-loader>
      </template>
      <template v-slot:item.name="{ item }">
        {{ `${item.lastName} ${item.firstName}` }}
      </template>

      <template v-slot:item.class="{ item }">
        {{ getClassName(item) }}
      </template>

      <template v-slot:item.gender="{ item }">
        {{ item.gender === "male" ? $t('common.male') : $t('common.female') }}
      </template>

      <template v-slot:item.age="{ item }">
        {{ $t('common.yearsOld', { n: getAgeBasedonBirthdate(item.dateOfBirth) }) }}
      </template>

      <template v-slot:item.isFamilyAssociated="{ item }">
        <v-chip color="success" size="small" v-if="item.isFamilyAssociated">
          <v-icon start size="small">mdi-check-circle</v-icon>
          {{ $t('common.completed') }}
        </v-chip>
        <v-chip v-else color="grey" size="small" variant="outlined">{{ $t('common.incomplete') }}</v-chip>
      </template>

      <template v-slot:item.edit="{ item }">
        <div class="d-flex justify-space-around align-center">
          <v-btn
            color="primary"
            variant="outlined"
            class="mr-2"
            size="small"
            :disabled="isFetching"
            :to="`/children/account/${item.childId}`"
          >{{ $t('children.accountSettings') }}</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            class="mr-2"
            size="small"
            :disabled="isFetching"
            :to="`/children/document/${item.childId}`"
          >{{ $t('children.childRecord') }}</v-btn>

          <v-btn
            color="#EE6190"
            theme="dark"
            variant="flat"
            size="small"
            :disabled="isFetching"
            :to="`/parent/${item.childId}/document`"
          >{{ $t('children.parentRecord') }}</v-btn>
        </div>
      </template>

      <template #bottom></template>
    </v-data-table>

    <v-dialog v-model="pdfDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t('children.invitationDownload') }}</v-card-title>
        <v-card-text></v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="pdfDialog = false" color="grey" text
                >{{ $t('common.cancel') }}</v-btn
              >
              <v-spacer />
              <v-btn
                class="mr-2"
                @click="downloadInvitationPdf(false)"
                :disabled="isFetching"
                color="primary"
                variant="outlined"
                >{{ $t('common.selectedOnly') }}</v-btn
              >
              <v-btn
                @click="downloadInvitationPdf(true)"
                :disabled="isFetching"
                color="primary"
                variant="flat"
                >{{ $t('common.allChildren') }}</v-btn
              >
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="memoruPdfDialog" max-width="600px">
    <!--MEMORU案内状が不要になったためコメントアウト-->
     <!--<v-card>
        <v-card-title>MEMORU案内状ダウンロード</v-card-title>
        <v-card-text></v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="pdfDialog = false" color="grey" text
                >キャンセル</v-btn
              >
              <v-spacer />
              <v-btn
                class="mr-2"
                @click="downloadInvitationPdf(false, 'memoru')"
                :disabled="isFetching"
                color="primary"
                variant="outlined"
                >{{ $t('common_actions.selectedChildrenOnly') }}</v-btn
              >
              <v-btn
                @click="downloadInvitationPdf(true, 'memoru')"
                :disabled="isFetching"
                color="primary"
                variant="flat"
                >{{ $t('common_actions.allChildren') }}</v-btn
              >
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
                -->
    </v-dialog>

    <v-dialog v-model="importDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t('children.batchImport') }}</v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="4" align-self="center"> {{ $t('common.csvFile') }} </v-col>
              <v-col cols="8" align-self="center">
                <v-file-input
                  v-model="importFile"
                  accept=".csv"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                >
                </v-file-input>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="importDialog = false" color="grey" text>
                {{ $t('common.cancel') }}
              </v-btn>
              <v-spacer />
              <v-btn
                @click="importCsv"
                :disabled="!importFile"
                :loading="isFetching"
                color="primary"
                variant="flat"
              >
                {{ $t('common.import') }}
              </v-btn>
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="exportChildrenDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t('children.childDownload') }}</v-card-title>
        <v-card-text></v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="pdfDialog = false" color="grey" text
                >{{ $t('common.cancel') }}</v-btn
              >
              <v-spacer />
              <v-btn
                @click="downloadChildrenCsv(false)"
                class="mr-2"
                :disabled="isFetching"
                color="primary"
                variant="outlined"
                >{{ $t('common.selectedOnly') }}</v-btn
              >
              <v-btn
                @click="downloadChildrenCsv(true)"
                :disabled="isFetching"
                color="primary"
                variant="flat"
                >{{ $t('common.allChildren') }}</v-btn
              >
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";

import { childrenListModule } from "@/store/dataModules/childrenListModule";
import LocalDataService from "@/service/LocalDataService";
import { classModule } from "@/store/dataModules/classModule";
import { NurseryChildResponse, NurseryClassResponse } from "chaild-api/lib";

import ClassPicker from "@/components/atoms/ClassPicker.vue";
import GenderPicker from "@/components/atoms/GenderPicker.vue";
import AgePicker from "@/components/atoms/AgePicker.vue";
import dayjs from "dayjs";
import ApiChildren from "@/api/ApiChildren";
import ApiImportExportStatus from "@/api/ApiImportExportStatus";
import ApiResource from "@/api/ApiResource";

interface DataType {
  isFetching: boolean;
  searchFilter: {
    isQuit: boolean;
  };
  selectedChildren: NurseryChildResponse[];
  pdfDialog: boolean;
  memoruPdfDialog: boolean;
  importFile: File | null;
  importDialog: boolean;
  exportChildrenDialog: boolean;
}

export default defineComponent({
  components: {
    ClassPicker,
    GenderPicker,
    AgePicker,
  },

  data: (): DataType => {
    return {
      isFetching: false,
      searchFilter: {
        isQuit: false,
      },
      selectedChildren: [],
      pdfDialog: false,
      memoruPdfDialog: false,
      importDialog: false,
      importFile: null,
      exportChildrenDialog: false,
    };
  },

  computed: {
    // Download import sample
    importChildSampleCsvUrl(): string {
      return `${process.env.VUE_APP_AWS_S3_URL}/csv_format/import_child_sample.csv`;
    },

    children(): NurseryChildResponse[] {
      if (this.searchFilter.isQuit) {
        // return childrenListModule.children.filter((c) => c.quitDate != null);
        return childrenListModule.children.filter((c) =>
          dayjs().isAfter(c.quitDate)
        );
      } else {
        return childrenListModule.children.filter(
          (c) => c.quitDate == null || !dayjs().isAfter(c.quitDate)
        );
      }
    },
    classes(): NurseryClassResponse[] {
      return classModule.classes;
    },
    classId: {
      get(): number | null {
        return childrenListModule.classId;
      },
      set(value: number | null) {
        childrenListModule.setClassId(value);
      },
    },
    firstName: {
      get(): string | null {
        return childrenListModule.firstName;
      },
      set(value: string | null) {
        childrenListModule.setFirstName(value);
      },
    },
    lastName: {
      get(): string | null {
        return childrenListModule.lastName;
      },
      set(value: string | null) {
        childrenListModule.setLastName(value);
      },
    },
    age: {
      get(): number | null {
        return childrenListModule.age;
      },
      set(value: number | null) {
        childrenListModule.setAge(value);
      },
    },
    gender: {
      get(): string | null {
        return childrenListModule.gender;
      },
      set(value: string | null) {
        childrenListModule.setGender(value);
      },
    },
    childrenTableHeaders: function () {
      return [
        { title: this.$t('children.childName') as string, value: "name" },
        { title: this.$t('children.className') as string, value: "class" },
        { title: this.$t('children.genderHeader') as string, value: "gender" },
        { title: this.$t('children.ageHeader') as string, value: "age" },
        { title: this.$t('children.associationStatus') as string, value: "isFamilyAssociated" },
        { title: '', value: "edit" },
      ];
    },
  },

  methods: {
    async search() {
      this.isFetching = true;
      await childrenListModule.search();
      this.isFetching = false;
    },
    getClassName(child: NurseryChildResponse) {
      if (child.nurseryClasses) {
        const latest =
          child.nurseryClasses.length > 0 ? child.nurseryClasses[0] : null;
        if (latest) {
          return latest.className;
        }
      }
      return "-";
    },
    getAgeBasedonBirthdate(birthdate: string): number {
      const thisYear = dayjs();
      return thisYear.diff(birthdate, "year");
    },
    async downloadInvitationPdf(
      isAll: boolean,
      type = "home" as "home" | "memoru"
    ) {
      if (!isAll && this.selectedChildren.length == 0) {
        alert(this.$t('common.noChildrenSelected') as string);
        this.pdfDialog = false;
        return;
      }
      this.isFetching = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        // 案内状DLリクエスト
        const invitationResponse = await ApiChildren.getInvitationPdf({
          nurseryId,
          childIds: isAll ? [] : this.selectedChildren.map((c) => c.childId),
          type,
        }).catch(() => {
          this.isFetching = false;
        });

        if (invitationResponse) {
          // ステータス取得
          const interval = setInterval(async () => {
            this.isFetching = true;
            const exportResponse = await ApiImportExportStatus.getStatus({
              statusId: invitationResponse.statusId,
              category: invitationResponse.category,
            }).catch(() => {
              clearInterval(interval);
              this.isFetching = false;
              this.pdfDialog = false;
            });
            if (exportResponse && exportResponse.status == "success") {
              clearInterval(interval);
              window.open(exportResponse.url, "_blank");
              this.isFetching = false;
            }
          }, 1000);
        }
      }
      this.isFetching = false;
    },
    async downloadChildrenCsv(isAll: boolean) {
      if (!isAll && this.selectedChildren.length == 0) {
        alert(this.$t('common.noChildrenSelected') as string);
        this.exportChildrenDialog = false;
        return;
      }
      this.isFetching = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        // 園児エクスポート
        const exportResponse = await ApiChildren.exportChildrenCsv({
          nurseryId,
          childrenIds: isAll ? [] : this.selectedChildren.map((c) => c.childId),
        }).catch(() => {
          this.isFetching = false;
        });

        if (exportResponse) {
          // ステータス取得
          const interval = setInterval(async () => {
            this.isFetching = true;
            const statusResponse = await ApiImportExportStatus.getStatus({
              statusId: exportResponse.statusId,
              category: exportResponse.category,
            }).catch(() => {
              clearInterval(interval);
              this.isFetching = false;
              this.exportChildrenDialog = false;
            });
            if (statusResponse && statusResponse.status == "success") {
              clearInterval(interval);
              window.open(statusResponse.url, "_blank");
              this.isFetching = false;
              this.exportChildrenDialog = false;
            }
          }, 1000);
        }
      }
      this.isFetching = false;
    },
    async importCsv() {
      this.isFetching = true;
      const nurseryId = LocalDataService.getNurseryId();
      const imageType = "childCsv";
      if (nurseryId && this.importFile) {
        // get presigned url
        const resourceResponse = await ApiResource.getPresignedUrl({
          imageType: imageType,
          nurseryId,
          filename: this.importFile.name,
        }).catch();
        if (resourceResponse) {
          // upload
          const uploadResponse = await ApiResource.uploadFile({
            url: resourceResponse.url,
            file: this.importFile,
          }).catch();
          if (uploadResponse) {
            // import
            const importResponse = await ApiChildren.importChildrenCsv({
              nurseryId,
              filePath: resourceResponse.filePath,
            }).catch(() => {
              this.isFetching = false;
              this.importDialog = false;
            });
            if (importResponse) {
              const interval = setInterval(async () => {
                this.isFetching = true;
                const statusResponse = await ApiImportExportStatus.getStatus({
                  statusId: importResponse.statusId,
                  category: importResponse.category,
                }).catch(() => {
                  clearInterval(interval);
                  this.isFetching = false;
                  this.importDialog = false;
                });
                if (statusResponse) {
                  if (statusResponse.status == "success") {
                    clearInterval(interval);
                    this.isFetching = false;
                    this.importDialog = false;
                    alert(this.$t('common.importCompleted') as string);
                    await this.search();
                  } else if (statusResponse.status == "failed") {
                    clearInterval(interval);
                    this.isFetching = false;
                    this.importDialog = false;
                    alert(this.$t('common.importFailed') as string);
                  }
                }
              }, 1000);
            }
          }
        }
      }
      this.isFetching = false;
    },
  },

  async mounted() {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      this.isFetching = true;
      await classModule.listClass({
        nurseryId: nurseryId,
      });
      this.isFetching = false;
    }
    await this.search();
  },
});
</script>
